import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { GstChoiceField } from "../../components/GstChoiceField";
import { GST_INCLUDED_18, gstPriceSuffix, type GstChoice } from "../../domain/gstChoice";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import { shortName } from "../../auth/scope";
import { formatInrPaise } from "../../domain/tiers";
import { newIdempotencyKey } from "../../api/pinOrders";
import {
  DEFAULT_PAY_MODE,
  PAY_MODES,
  inclusiveSplit30d,
  payModeLabel,
  setBuyerPayMode,
  setPayMode,
  type PayMode,
  type PayModeView,
  type SellerWallet,
  WALLET_KIND_LABEL,
  WALLET_STATUS_LABEL,
  bandGross30d,
  canInvoiceTopup,
  decideTopup,
  getTopupInvoice,
  itemKeyError,
  listWalletItems,
  putWalletItem,
  stopWalletItem,
  UPSTREAM_ITEM_SUGGESTIONS,
  unpricedSuggestions,
  itemGross,
  getWallet,
  listSellerWallets,
  listWalletBands,
  putWalletBand,
  raiseTopupInvoice,
  recordTopup,
  rupeesToPaise,
  signedPaise,
  stopWalletBand,
  type Wallet,
  type WalletBand,
  type WalletEntry,
  type WalletItem,
  type WalletItemBilling,
  ledgerTime,
} from "../../api/wallet";

/**
 * Operator wallet screens (owner 2026-10-15): the buyer's wallet on its Pins
 * page, and the seller's rate card and top-ups to confirm on its Pin orders
 * page. Pins are spent first; the wallet pays only when no pin covers a new
 * user or recharge.
 */

const inr = (paise: number) => formatInrPaise(paise / 100);
const when = ledgerTime;
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));

/* ------------------------------------------------------------ buyer */

export function BuyerWalletSection({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [adding, setAdding] = useState<string | null>(null);
  const view = useQuery({
    queryKey: ["wallet", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getWallet(identity, ns, signal),
    retry: false,
  });
  if (view.error) return <Alert severity="error">Funds: {errText(view.error)}</Alert>;
  if (!view.data || !view.data.eligible) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="subtitle1">Funds</Typography>
      <Typography variant="body2" color="text.secondary">
        Money you pay your seller in advance. A new user or recharge is paid by one of your pins or from here, in the order you choose below; Static IP and
        other items from your seller are always paid from here. With nothing left to pay, it is refused — nobody already connected is cut off.
      </Typography>
      <PayModeCard ns={ns} view={view.data.paymode ?? DEFAULT_PAY_MODE} />
      {view.data.wallets.map((w) => (
        <WalletCard key={w.sellernamespace} wallet={w} onAdd={() => setAdding(w.sellernamespace)} />
      ))}
      {adding && (
        <TopupDialog
          ns={ns}
          seller={adding}
          onClose={() => setAdding(null)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ["wallet", ns] });
            setAdding(null);
          }}
        />
      )}
    </Box>
  );
}

/** The operator's own pay order (owner 2026-10-18). */
function PayModeCard({ ns, view }: { ns: string; view: PayModeView }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: (m: PayMode) => setPayMode(identity, ns, m),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["wallet", ns] }),
  });
  const current = PAY_MODES.find((p) => p.mode === view.paymode);
  return (
    <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        How plans are paid for
      </Typography>
      <TextField
        select
        size="small"
        label="Pay order"
        value={view.paymode}
        disabled={view.locked || run.isPending}
        onChange={(e) => run.mutate(e.target.value as PayMode)}
        helperText={view.locked ? "Your seller has set this and locked it. Ask your seller to change it." : current?.help ?? " "}
        sx={{ maxWidth: 360 }}
      >
        {PAY_MODES.map((p) => (
          <MenuItem key={p.mode} value={p.mode}>
            {p.label}
          </MenuItem>
        ))}
      </TextField>
      {view.locked && <Chip size="small" color="warning" label="Locked by your seller" sx={{ alignSelf: "flex-start" }} />}
      {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
    </Paper>
  );
}

/** The seller setting a buyer's pay order, and locking it or giving the choice back. */
function BuyerPayModeDialog({ ns, wallet, onClose, onDone }: { ns: string; wallet: SellerWallet; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const now = wallet.paymode ?? DEFAULT_PAY_MODE;
  const [mode, setMode] = useState<PayMode>(now.paymode);
  const [locked, setLocked] = useState(now.locked);
  const run = useMutation({ mutationFn: () => setBuyerPayMode(identity, ns, wallet.holdernamespace, mode, locked), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>How {shortName(wallet.holdernamespace)} pays for plans</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField select label="Pay order" value={mode} onChange={(e) => setMode(e.target.value as PayMode)} helperText={PAY_MODES.find((p) => p.mode === mode)?.help ?? " "} fullWidth>
          {PAY_MODES.map((p) => (
            <MenuItem key={p.mode} value={p.mode}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel control={<Checkbox checked={locked} onChange={(e) => setLocked(e.target.checked)} />} label="Lock it — the buyer cannot change it" />
        {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={run.isPending} onClick={() => run.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WalletCard({ wallet: w, onAdd }: { wallet: Wallet; onAdd: () => void }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Available funds with {shortName(w.sellernamespace)}
          </Typography>
          <Typography variant="h5">{inr(w.balancepaise)}</Typography>
        </Box>
        {w.pendingpaise > 0 && <Chip color="warning" label={`${inr(w.pendingpaise)} waiting for ${shortName(w.sellernamespace)} to confirm`} />}
        <Button variant="contained" onClick={onAdd}>
          Add funds
        </Button>
      </Box>
      {w.bands.length === 0 ? (
        <Alert severity="info">{shortName(w.sellernamespace)} has not published funds prices yet, so the funds cannot pay for anything.</Alert>
      ) : (
        <BandTable bands={w.bands} />
      )}
      <Typography variant="subtitle2">Funds history</Typography>
      <EntryTable entries={w.entries} showHolder={false} empty="Nothing yet." />
    </Paper>
  );
}

function TopupDialog({ ns, seller, onClose, onDone }: { ns: string; seller: string; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const key = useMemo(() => newIdempotencyKey(), []);
  const paise = rupeesToPaise(amount);
  const refErr = reference.trim() === "" ? "The UPI reference, bank UTR or cheque number" : reference.trim().length > 128 ? "Up to 128 characters" : null;
  const run = useMutation({
    mutationFn: () => recordTopup(identity, ns, { seller, amountpaise: paise as number, paymentreference: reference, idempotencykey: key }),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add funds</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Typography variant="body2">
          Pay {shortName(seller)} first, then record it here. It is added when {shortName(seller)} confirms the payment — recording it alone adds nothing.
        </Typography>
        <TextField
          label="Amount paid (₹)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          error={amount !== "" && typeof paise === "string"}
          helperText={amount !== "" && typeof paise === "string" ? paise : " "}
          inputMode="decimal"
          fullWidth
        />
        <TextField
          label="Payment reference"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          error={reference !== "" && refErr !== null && reference.trim().length > 128}
          helperText={refErr ?? " "}
          fullWidth
        />
        {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={typeof paise === "string" || reference.trim() === "" || reference.trim().length > 128 || run.isPending} onClick={() => run.mutate()}>
          Record payment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------------------------------ seller */

export function SellerWalletSection({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<WalletBand | "new" | null>(null);
  const [rejecting, setRejecting] = useState<WalletEntry | null>(null);
  const [invoicing, setInvoicing] = useState<WalletEntry | null>(null);
  const [ordering, setOrdering] = useState<SellerWallet | null>(null);
  const bands = useQuery({
    queryKey: ["walletbands", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listWalletBands(identity, ns, signal),
    retry: false,
  });
  const wallets = useQuery({
    queryKey: ["wallets-incoming", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listSellerWallets(identity, ns, signal),
    retry: false,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["walletbands", ns] });
    void qc.invalidateQueries({ queryKey: ["wallets-incoming", ns] });
  };
  const confirm = useMutation({ mutationFn: (id: number) => decideTopup(identity, ns, id, true), onSuccess: refresh });
  const stop = useMutation({ mutationFn: (name: string) => stopWalletBand(identity, ns, name), onSuccess: refresh });
  const pending = (wallets.data?.entries ?? []).filter((e) => e.kind === "topup" && e.status === "pending");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" component="h2">
            Funds prices
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When a buyer&apos;s funds pay for a new user or recharge (in the buyer&apos;s pay order: pins first unless it chose otherwise), the price is
            taken from the money it paid you in advance: the smallest band that covers the plan&apos;s speed, per 30 days, plus GST — or GST included, if
            you say so. A plan faster than every band is refused. A new price applies from the next activation.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setEditing("new")}>
          Add a band
        </Button>
      </Box>
      {bands.error && <Alert severity="error">{errText(bands.error)}</Alert>}
      {stop.error && <Alert severity="error">{errText(stop.error)}</Alert>}
      {bands.data && bands.data.length === 0 && <Alert severity="info">No bands yet: no buyer's funds can pay for anything until you add one.</Alert>}
      {bands.data && bands.data.length > 0 && <BandTable bands={bands.data} onEdit={(b) => setEditing(b)} onStop={(b) => stop.mutate(b.name)} />}

      <ItemPricesSection ns={ns} />

      <Typography variant="h6" component="h2">
        Buyer funds
      </Typography>
      {wallets.error && <Alert severity="error">{errText(wallets.error)}</Alert>}
      {confirm.error && <Alert severity="error">{errText(confirm.error)}</Alert>}
      {pending.length > 0 && (
        <Alert severity="info">
          {pending.length === 1 ? "1 payment is" : `${pending.length} payments are`} waiting for you to confirm you received the money. Confirming adds it to
          the buyer&apos;s funds.
        </Alert>
      )}
      {pending.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Buyer</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell>Recorded</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pending.map((e) => (
                <TableRow key={e.id}>
                  <TableCell>{shortName(e.holdernamespace)}</TableCell>
                  <TableCell align="right">{inr(e.amountpaise)}</TableCell>
                  <TableCell>{e.paymentreference}</TableCell>
                  <TableCell>{when(e.createdat)}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="contained" disabled={confirm.isPending} onClick={() => confirm.mutate(e.id)}>
                        Confirm received
                      </Button>
                      <Button size="small" color="error" onClick={() => setRejecting(e)}>
                        Reject
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {wallets.data && wallets.data.wallets.length === 0 && <Typography color="text.secondary">No buyer has funds with you yet.</Typography>}
      {wallets.data && wallets.data.wallets.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Buyer</TableCell>
                <TableCell align="right">Available funds</TableCell>
                <TableCell align="right">Waiting to confirm</TableCell>
                <TableCell>Pays for plans</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {wallets.data.wallets.map((w) => (
                <TableRow key={w.holdernamespace}>
                  <TableCell>{shortName(w.holdernamespace)}</TableCell>
                  <TableCell align="right">{inr(w.balancepaise)}</TableCell>
                  <TableCell align="right">{w.pendingpaise > 0 ? inr(w.pendingpaise) : "—"}</TableCell>
                  <TableCell>
                    {payModeLabel((w.paymode ?? DEFAULT_PAY_MODE).paymode)}
                    {w.paymode?.locked && <Chip size="small" label="Locked" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => setOrdering(w)}>
                      Change
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {wallets.data && wallets.data.entries.length > 0 && <EntryTable entries={wallets.data.entries} showHolder empty="" onInvoice={setInvoicing} />}

      {editing && <BandDialog ns={ns} band={editing === "new" ? null : editing} onClose={() => setEditing(null)} onDone={() => { refresh(); setEditing(null); }} />}
      {invoicing && <TopupInvoiceDialog ns={ns} entry={invoicing} onClose={() => setInvoicing(null)} />}
      {ordering && <BuyerPayModeDialog ns={ns} wallet={ordering} onClose={() => setOrdering(null)} onDone={() => { refresh(); setOrdering(null); }} />}
      {rejecting && <RejectDialog ns={ns} entry={rejecting} onClose={() => setRejecting(null)} onDone={() => { refresh(); setRejecting(null); }} />}
    </Box>
  );
}

function BandDialog({ ns, band, onClose, onDone }: { ns: string; band: WalletBand | null; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [name, setName] = useState(band?.name ?? "");
  const [speed, setSpeed] = useState(band ? String(band.maxspeedmbps) : "");
  const [price, setPrice] = useState(band ? (band.pricepaiseper30d / 100).toFixed(2) : "");
  const initialGst: GstChoice | null = band ? { gstratebp: band.gstratebp, priceincludesgst: Boolean(band.priceincludesgst) } : null;
  const [gstc, setGstc] = useState<GstChoice>(initialGst ?? GST_INCLUDED_18);
  const inclusive = gstc.priceincludesgst;
  const bp = gstc.gstratebp;
  const paise = rupeesToPaise(price, 0.01, 1_000_000);
  const speedErr = /^\d+$/.test(speed.trim()) && Number(speed) >= 1 && Number(speed) <= 100000 ? null : "Whole Mbps, 1 to 100000";
  const nameErr = /^[A-Za-z0-9 ._()+-]{1,64}$/.test(name.trim()) ? null : "Up to 64 letters, digits, spaces and . _ ( ) + -";
  const ok = !nameErr && !speedErr && typeof paise === "number";
  const run = useMutation({
    mutationFn: () =>
      putWalletBand(identity, ns, { name, maxspeedmbps: Number(speed), pricepaiseper30d: paise as number, gstratebp: bp, priceincludesgst: inclusive }),
    onSuccess: onDone,
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{band ? `Change ${band.name}` : "Add a band"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField label="Name" value={name} disabled={Boolean(band)} onChange={(e) => setName(e.target.value)} error={name !== "" && Boolean(nameErr)} helperText={name !== "" ? nameErr ?? " " : "e.g. Up to 100 Mbps"} fullWidth />
        <TextField label="Covers plans up to (Mbps)" value={speed} onChange={(e) => setSpeed(e.target.value)} error={speed !== "" && Boolean(speedErr)} helperText={speed !== "" ? speedErr ?? " " : " "} fullWidth />
        <TextField
          label={`Price per 30 days${bp === 0 ? "" : inclusive ? ", GST included" : ", before GST"} (₹)`}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          error={price !== "" && typeof paise === "string"}
          helperText={price !== "" && typeof paise === "string" ? paise : "A 90-day plan is charged 3 times this, a 1-day plan 1/30"}
          inputMode="decimal"
          fullWidth
        />
        <GstChoiceField value={gstc} onChange={setGstc} initial={initialGst} />
        {ok && (
          <Typography variant="body2">
            A 30-day activation will take {inr(bandGross30d({ pricepaiseper30d: paise as number, gstratebp: bp, priceincludesgst: inclusive }))} from
            the buyer&apos;s funds
            {inclusive &&
              (() => {
                const sp = inclusiveSplit30d(paise as number, bp);
                return ` (${inr(sp.net)} + ${inr(sp.gst)} GST)`;
              })()}
            .
          </Typography>
        )}
        {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ok || run.isPending} onClick={() => run.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RejectDialog({ ns, entry, onClose, onDone }: { ns: string; entry: WalletEntry; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [reason, setReason] = useState("");
  const run = useMutation({ mutationFn: () => decideTopup(identity, ns, entry.id, false, reason), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Reject this payment</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <Typography variant="body2">
          {inr(entry.amountpaise)} from {shortName(entry.holdernamespace)}, reference {entry.paymentreference}. Nothing is added to the funds. The buyer sees
          your reason.
        </Typography>
        <TextField label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} helperText="e.g. No payment with this UTR received" fullWidth />
        {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" disabled={reason.trim() === "" || reason.trim().length > 255 || run.isPending} onClick={() => run.mutate()}>
          Reject
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function TopupInvoiceDialog({ ns, entry, onClose }: { ns: string; entry: WalletEntry; onClose: () => void }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const key = ["wallettopup-invoice", ns, entry.id];
  const invoice = useQuery({
    queryKey: key,
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => getTopupInvoice(identity, ns, entry.id, signal),
    retry: false,
  });
  const raise = useMutation({ mutationFn: () => raiseTopupInvoice(identity, ns, entry.id), onSuccess: (inv) => qc.setQueryData(key, inv) });
  const inv = invoice.data;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Invoice for {inr(entry.amountpaise)} added to the funds</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {shortName(entry.holdernamespace)} — reference {entry.paymentreference}, confirmed {when(entry.decidedat)}
          </Typography>
          {invoice.isLoading && <Typography color="text.secondary">Loading…</Typography>}
          {invoice.error && <Alert severity="error">{errText(invoice.error)}</Alert>}
          {!invoice.isLoading && !inv && (
            <Alert severity="info">
              No invoice yet. It totals exactly the money received, with the GST inside it, and numbers a document in your own invoice series —
              once. Activations paid from this money are not invoiced again.
            </Alert>
          )}
          {inv && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="h6">{inv.invoicenumber}</Typography>
              <Typography variant="body2" color="text.secondary">
                {inv.supplierlegalname}
                {inv.suppliergstin ? ` · ${inv.suppliergstin}` : ""}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                To {inv.buyername}
                {inv.buyergstin ? ` · ${inv.buyergstin}` : " · no GSTIN"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Taxable {inr(Math.round(inv.taxablevalue * 100))}
              </Typography>
              <Typography variant="body2">
                GST {inr(Math.round(inv.totaltax * 100))}
                {inv.totaligst > 0 ? " (IGST)" : inv.totaltax > 0 ? " (CGST + SGST)" : ""}
              </Typography>
              <Typography variant="h6">Total {inr(Math.round(inv.totalamount * 100))}</Typography>
            </Paper>
          )}
          {raise.error && <Alert severity="error">{topupInvoiceRefusal(raise.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!inv && !invoice.isLoading && (
          <Button variant="contained" disabled={raise.isPending} onClick={() => raise.mutate()}>
            Raise invoice
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function topupInvoiceRefusal(e: unknown): string {
  const reason = errText(e);
  if (e instanceof ApiError && e.status === 403) {
    return `Not allowed to issue this invoice: the invoicing company must hold a licence to invoice internet access (SAC 998422). Server: ${reason}`;
  }
  if (e instanceof ApiError && e.status === 409) return `Only a confirmed payment can be invoiced. Server: ${reason}`;
  return `Could not raise the invoice. Check that both companies have a legal name and a registered office on record. Server: ${reason}`;
}

/* ------------------------------------------------------------ item prices */

function ItemPricesSection({ ns }: { ns: string }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<WalletItem | "new" | null>(null);
  const items = useQuery({
    queryKey: ["walletitems", ns],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listWalletItems(identity, ns, signal),
    retry: false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["walletitems", ns] });
  const stop = useMutation({ mutationFn: (key: string) => stopWalletItem(identity, ns, key), onSuccess: refresh });
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1">Item prices</Typography>
          <Typography variant="body2" color="text.secondary">
            What you charge a buyer&apos;s funds for items beyond the plan — a Static IP, an OTT pack. Pins never pay for these. A recurring item is
            charged per 30 days at every renewal; if the funds cannot pay, that item is refused and the plan still sells. An unpaid recurring item ends
            3 days after its paid period.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => setEditing("new")}>
          Add an item
        </Button>
      </Box>
      {items.error && <Alert severity="error">{errText(items.error)}</Alert>}
      {stop.error && <Alert severity="error">{errText(stop.error)}</Alert>}
      {items.data && items.data.length === 0 && <Typography color="text.secondary">No item prices yet.</Typography>}
      {items.data && items.data.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>Key</TableCell>
                <TableCell>Charged</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">With GST</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.data.map((it) => (
                <TableRow key={it.id} sx={it.active ? undefined : { opacity: 0.5 }}>
                  <TableCell>
                    {it.name}
                    {!it.active && <Chip size="small" label="Stopped" sx={{ ml: 1 }} />}
                  </TableCell>
                  <TableCell>
                    <code>{it.itemkey}</code>
                  </TableCell>
                  <TableCell>{it.billing === "recurring" ? "Per 30 days, every renewal" : "Once"}</TableCell>
                  <TableCell align="right">
                    {inr(it.pricepaise)}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {gstPriceSuffix({ gstratebp: it.gstratebp, priceincludesgst: Boolean(it.priceincludesgst) })}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{inr(itemGross(it))}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" onClick={() => setEditing(it)}>
                        {it.active ? "Change" : "Restart"}
                      </Button>
                      {it.active && (
                        <Button size="small" color="error" disabled={stop.isPending} onClick={() => stop.mutate(it.itemkey)}>
                          Stop
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {editing && <ItemDialog ns={ns} item={editing === "new" ? null : editing} priced={items.data ?? []} onClose={() => setEditing(null)} onDone={() => { refresh(); setEditing(null); }} />}
    </Box>
  );
}

const OTHER_ITEM = "__other__";

function ItemDialog({ ns, item, priced, onClose, onDone }: { ns: string; item: WalletItem | null; priced: WalletItem[]; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [key, setKey] = useState(item?.itemkey ?? "");
  const [other, setOther] = useState(false);
  const free = unpricedSuggestions(priced);
  const [name, setName] = useState(item?.name ?? "");
  const [billing, setBilling] = useState<WalletItemBilling>(item?.billing ?? "recurring");
  const [price, setPrice] = useState(item ? (item.pricepaise / 100).toFixed(2) : "");
  const initialGst: GstChoice | null = item ? { gstratebp: item.gstratebp, priceincludesgst: Boolean(item.priceincludesgst) } : null;
  const [gstc, setGstc] = useState<GstChoice>(initialGst ?? GST_INCLUDED_18);
  const paise = rupeesToPaise(price, 0.01, 1_000_000);
  const taken = !item && priced.some((it) => it.itemkey === key.trim());
  const keyErr = taken ? "Already in your list: use Change (or Restart) on it" : itemKeyError(key);
  const nameErr = /^[A-Za-z0-9 ._()+-]{1,64}$/.test(name.trim()) ? null : "Up to 64 letters, digits, spaces and . _ ( ) + -";
  const ok = !keyErr && !nameErr && typeof paise === "number";
  const run = useMutation({
    mutationFn: () => putWalletItem(identity, ns, { itemkey: key, name, billing, pricepaise: paise as number, ...gstc }),
    onSuccess: onDone,
  });
  const pick = (k: string) => {
    if (k === OTHER_ITEM) {
      setOther(true);
      setKey("");
      return;
    }
    setOther(false);
    const s = UPSTREAM_ITEM_SUGGESTIONS.find((x) => x.key === k);
    setKey(k);
    if (s) {
      if (!name) setName(s.name);
      setBilling(s.billing);
    }
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{item ? `Change ${item.name}` : "Add an item"}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        {item ? (
          <TextField label="Item key" value={key} disabled fullWidth />
        ) : (
          <TextField
            select
            label="Item"
            value={other ? OTHER_ITEM : key}
            onChange={(e) => pick(e.target.value)}
            helperText={free.length === 0 && !other ? "Every suggested item is already in your list; pick Another item to add a new one" : "Items already in your list are not offered again"}
            fullWidth
          >
            {free.map((s) => (
              <MenuItem key={s.key} value={s.key}>
                {s.name} ({s.key})
              </MenuItem>
            ))}
            <MenuItem value={OTHER_ITEM}>Another item (type its key)…</MenuItem>
          </TextField>
        )}
        {other && (
          <TextField
            label="Item key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            error={key !== "" && Boolean(keyErr)}
            helperText={key !== "" && keyErr ? keyErr : "e.g. ott:partner:premium_30. Your buyers pick it by this key."}
            autoFocus
            fullWidth
          />
        )}
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} error={name !== "" && Boolean(nameErr)} helperText={name !== "" ? nameErr ?? " " : " "} fullWidth />
        <TextField select label="Charged" value={billing} onChange={(e) => setBilling(e.target.value as WalletItemBilling)} fullWidth>
          <MenuItem value="recurring">Per 30 days, at every renewal</MenuItem>
          <MenuItem value="one_time">Once</MenuItem>
        </TextField>
        <TextField
          label={`${billing === "recurring" ? "Price per 30 days" : "Price"}${gstc.gstratebp === 0 ? "" : gstc.priceincludesgst ? ", GST included" : ", before GST"} (₹)`}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          error={price !== "" && typeof paise === "string"}
          helperText={price !== "" && typeof paise === "string" ? paise : " "}
          inputMode="decimal"
          fullWidth
        />
        <GstChoiceField value={gstc} onChange={setGstc} initial={initialGst} />
        {ok && (
          <Typography variant="body2">
            {billing === "recurring" ? "A 30-day renewal" : "Each sale"} takes {inr(itemGross({ pricepaise: paise as number, ...gstc }))} from the
            buyer&apos;s funds.
          </Typography>
        )}
        {run.error && <Alert severity="error">{errText(run.error)}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ok || run.isPending} onClick={() => run.mutate()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ------------------------------------------------------------ shared */

function BandTable({ bands, onEdit, onStop }: { bands: WalletBand[]; onEdit?: (b: WalletBand) => void; onStop?: (b: WalletBand) => void }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Band</TableCell>
            <TableCell>Covers plans up to</TableCell>
            <TableCell align="right">Per 30 days</TableCell>
            <TableCell align="right">With GST</TableCell>
            {onEdit && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {bands.map((b) => (
            <TableRow key={b.id} sx={b.active ? undefined : { opacity: 0.5 }}>
              <TableCell>
                {b.name}
                {!b.active && <Chip size="small" label="Stopped" sx={{ ml: 1 }} />}
              </TableCell>
              <TableCell>{b.maxspeedmbps} Mbps</TableCell>
              <TableCell align="right">{inr(b.priceincludesgst ? inclusiveSplit30d(b.pricepaiseper30d, b.gstratebp).net : b.pricepaiseper30d)}</TableCell>
              <TableCell align="right">
                {inr(bandGross30d(b))}{" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  ({gstPriceSuffix({ gstratebp: b.gstratebp, priceincludesgst: Boolean(b.priceincludesgst) })})
                </Typography>
              </TableCell>
              {onEdit && (
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => onEdit(b)}>
                      {b.active ? "Change" : "Restart"}
                    </Button>
                    {b.active && onStop && (
                      <Button size="small" color="error" onClick={() => onStop(b)}>
                        Stop
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EntryTable({ entries, showHolder, empty, onInvoice }: { entries: WalletEntry[]; showHolder: boolean; empty: string; onInvoice?: (e: WalletEntry) => void }) {
  if (entries.length === 0) return empty ? <Typography color="text.secondary">{empty}</Typography> : null;
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            {showHolder && <TableCell>Buyer</TableCell>}
            <TableCell>What</TableCell>
            <TableCell>Detail</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell>Status</TableCell>
            {onInvoice && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((e) => {
            const signed = signedPaise(e);
            const detail =
              e.kind === "deduction" && e.itemkey
                ? `${e.itemkey}${e.quantity && e.quantity > 1 ? ` × ${e.quantity}` : ""}${e.termdays ? ` · ${e.termdays} days` : ""} · ${inr(e.netpaise ?? 0)} + GST ${inr(e.gstpaise ?? 0)}`
                : e.kind === "deduction"
                ? `${e.groupname ?? ""} · ${e.bandname ?? ""} · ${e.speedmbps ?? "?"} Mbps × ${e.termdays ?? "?"} days · ${inr(e.netpaise ?? 0)} + GST ${inr(e.gstpaise ?? 0)}`
                : [e.paymentreference, e.decisionreason].filter(Boolean).join(" · ");
            return (
              <TableRow key={e.id}>
                <TableCell>{when(e.createdat)}</TableCell>
                {showHolder && <TableCell>{shortName(e.holdernamespace)}</TableCell>}
                <TableCell>{WALLET_KIND_LABEL[e.kind]}</TableCell>
                <TableCell>{detail}</TableCell>
                <TableCell align="right" sx={{ color: e.status !== "confirmed" ? "text.secondary" : signed < 0 ? "error.main" : "success.main" }}>
                  {signed < 0 ? "−" : "+"}
                  {inr(Math.abs(signed))}
                </TableCell>
                <TableCell>{WALLET_STATUS_LABEL[e.status]}</TableCell>
                {onInvoice && (
                  <TableCell align="right">
                    {canInvoiceTopup(e) && (
                      <Button size="small" onClick={() => onInvoice(e)}>
                        Invoice
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
